import { useCallback, useEffect, useRef, useState } from "react"
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react"
import {
	IconArrowLeftOutline18 as ArrowLeftIcon,
	IconArrowRightOutline18 as ArrowRightIcon,
	IconChevronLeftOutline18 as ChevronLeftIcon,
	IconChevronRightOutline18 as ChevronRightIcon,
	IconImagePlusOutline18 as ImagePlusIcon,
	IconTrashOutline18 as TrashIcon,
} from "nucleo-ui-outline-18"

import type { GalleryImage } from "@productlane/lib/src/tiptap/extensions/image-gallery-extension"
import { cn } from "@productlane/lib/src/cn"
import {
	ImageGalleryExtension,
	toGalleryImages,
} from "@productlane/lib/src/tiptap/extensions/image-gallery-extension"
import { Badge, IconButton, Tooltip } from "@productlane/ui"

import type { NodeViewProps } from "@tiptap/react"
import { stopMediaNodeViewEvent } from "./media-drag"

function GalleryNodeView({
	node,
	updateAttributes,
	deleteNode,
	editor,
}: NodeViewProps) {
	const images = toGalleryImages(node.attrs.images)
	const trackRef = useRef<HTMLDivElement | null>(null)
	const [index, setIndex] = useState(0)

	// The carousel loops, so the last image steps back to the first.
	const scrollToSlide = useCallback((next: number, total: number) => {
		const track = trackRef.current
		if (!track || total === 0) return
		const slide = track.children[(next + total) % total] as
			| HTMLElement
			| undefined
		if (!slide) return
		track.scrollTo({
			left: slide.offsetLeft - track.offsetLeft,
			behavior: "smooth",
		})
	}, [])

	useEffect(() => {
		const track = trackRef.current
		if (!track) return
		const onScroll = () => {
			const step = track.scrollWidth / Math.max(track.children.length, 1)
			if (step <= 0) return
			setIndex(
				Math.min(
					track.children.length - 1,
					Math.max(0, Math.round(track.scrollLeft / step)),
				),
			)
		}
		track.addEventListener("scroll", onScroll, { passive: true })
		return () => track.removeEventListener("scroll", onScroll)
	}, [])

	const current = Math.min(index, Math.max(images.length - 1, 0))

	const setImages = useCallback(
		(next: GalleryImage[]) => {
			if (next.length === 0) {
				deleteNode()
				return
			}
			updateAttributes({ images: next })
		},
		[deleteNode, updateAttributes],
	)

	const removeCurrent = () => {
		setImages(images.filter((_, i) => i !== current))
		setIndex(Math.max(0, current - 1))
	}

	const move = (offset: number) => {
		const target = current + offset
		if (target < 0 || target >= images.length) return
		const next = [...images]
		const moved = next[current]
		const swapped = next[target]
		if (!moved || !swapped) return
		next[current] = swapped
		next[target] = moved
		setImages(next)
		setIndex(target)
		scrollToSlide(target, images.length)
	}

	const addImages = () => {
		window.dispatchEvent(
			new CustomEvent("gallery-upload-request", {
				detail: {
					onImages: (uploaded: GalleryImage[]) =>
						setImages([...images, ...uploaded]),
				},
			}),
		)
	}

	return (
		<NodeViewWrapper className="pl-gallery group relative my-8">
			<div ref={trackRef} className="pl-gallery-track">
				{images.map((image, imageIndex) => (
					<div className="pl-gallery-slide" key={`${image.src}-${imageIndex}`}>
						<img
							data-drag-preview={imageIndex === current ? "" : undefined}
							src={image.src}
							alt={image.alt ?? ""}
							className="pl-gallery-image"
							draggable={false}
						/>
					</div>
				))}
			</div>

			{images.length > 1 && (
				<>
					<IconButton
						variant="glass"
						size="lg"
						isRounded
						Icon={ChevronLeftIcon}
						aria-label="Previous image"
						onClick={() => scrollToSlide(current - 1, images.length)}
						className="absolute left-3 top-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
					/>
					<IconButton
						variant="glass"
						size="lg"
						isRounded
						Icon={ChevronRightIcon}
						aria-label="Next image"
						onClick={() => scrollToSlide(current + 1, images.length)}
						className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
					/>
					<Badge
						variant="dark-gray"
						className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2"
					>
						{current + 1} / {images.length}
					</Badge>
				</>
			)}

			{editor.isEditable && (
				<div
					className={cn(
						"absolute right-3 top-3 flex items-center gap-1 rounded-lg bg-white/90 p-1 opacity-0 shadow-sm ring-1 ring-black/5 backdrop-blur",
						"transition-opacity duration-150 group-hover:opacity-100 dark:bg-gray-900/90 dark:ring-white/10",
					)}
				>
					<Tooltip label="Move image left">
						<IconButton
							variant="ghost"
							size="md"
							Icon={ArrowLeftIcon}
							aria-label="Move image left"
							disabled={current === 0}
							onClick={() => move(-1)}
						/>
					</Tooltip>
					<Tooltip label="Move image right">
						<IconButton
							variant="ghost"
							size="md"
							Icon={ArrowRightIcon}
							aria-label="Move image right"
							disabled={current === images.length - 1}
							onClick={() => move(1)}
						/>
					</Tooltip>
					<Tooltip label="Add images">
						<IconButton
							variant="ghost"
							size="md"
							Icon={ImagePlusIcon}
							aria-label="Add images"
							onClick={addImages}
						/>
					</Tooltip>
					<Tooltip label="Remove image">
						<IconButton
							variant="ghost"
							size="md"
							Icon={TrashIcon}
							aria-label="Remove image"
							onClick={removeCurrent}
						/>
					</Tooltip>
				</div>
			)}
		</NodeViewWrapper>
	)
}

export const ImageGalleryWithNodeView = ImageGalleryExtension.extend({
	addNodeView() {
		return ReactNodeViewRenderer(GalleryNodeView, {
			stopEvent: ({ event }) =>
				stopMediaNodeViewEvent(event, this.editor.isEditable),
		})
	},
})
